import React, { useContext, useEffect } from 'react';
import Flex, { FlexDirection, FlexJustification } from '../../common/Flex';
import { useAppDispatch, useAppSelector, useTranslate } from '../../../hooks/common';
import { useState } from 'react';
import { getGearboxInertiaSubtitutedMax, getInertiaUnit, getLoadInertiaMax, getN2Max, powerRound, fixNumberZeroFormat, setStringDecimalCount, showInfoModal } from '../../../services';
import { GearboxData, GearboxDataInput } from '../../../models';
import { setGearboxInertiaDialog, setGearboxInertiaError, setGearboxInputData, setLoadInertiaError, setLoadInertiaLowDialog, setN2Error, setTags } from '../../../store';
import { MotorDriveConnection } from '../../../enums';
import { DEFAULT_GEARBOX_INERTIA, DOUBLE_REGEX, TRANSLATIONS } from '../../../constants';
import Accordion from '../../common/Accordion';
import Icon, { IconType } from '../../common/Icon';
import MultilineTooltip from '../../common/MultilineTooltip';
import Select from '../../common/Select';
import TextInput from '../../common/TextInput';
import gearboxDirectImg from '../../../../assets/images/gearbox/direct.png';
import withGearboxImg from '../../../../assets/images/gearbox/withGearbox.png';
import { useHook } from '../../../hooks';
import { HomeContext } from '../Home';
import { showStatusMessage } from '@innomotics/innomotics-build-react-lib';

interface GearboxInputDataProps {
    disabled: boolean;
}

const GearboxInputData = ({ disabled }: GearboxInputDataProps) => {
    const translate = useTranslate();
    const dispatch = useAppDispatch();
    const calculationResponse = useAppSelector(state => state.motor.calculationResponse);
    const mesRequest = useAppSelector(state => state.motor.mesRequest);
    const gearboxInputData = useAppSelector(state => state.gearbox.gearboxInputData);
    const gearboxInertiaError = useAppSelector(state => state.gearbox.gearboxInertiaError);
    const n2Error = useAppSelector(state => state.gearbox.n2Error);
    const loadInertiaError = useAppSelector(state => state.gearbox.loadInertiaError);
    const renderGearboxInertiaDialog = useAppSelector(state => state.gearbox.renderGearboxInertiaDialog);
    const renderLoadInertiaLowDialog = useAppSelector(state => state.load.showLoadInertiaLowDialog);
    const [accordionOpen, toggleAccordion] = useState(true);
    const { getMainBoundary } = useContext(HomeContext);
    const { onGearboxChange } = useHook(x => x.gearboxData)();

    const isGearboxConnected = mesRequest.gearboxData.motorDriveConnection === MotorDriveConnection.WithGearbox;

    const unit = getInertiaUnit(mesRequest.unitSystem);

    useEffect(() => {
        if (renderGearboxInertiaDialog) {
            showGearboxInertiaDialog();
        }
        if (renderLoadInertiaLowDialog) {
            showLoadInertiaDialog();
        }
    }, [renderGearboxInertiaDialog, renderLoadInertiaLowDialog]);

    const handleMotorDriveConnectionChange = async (value: { [key in MotorDriveConnection]?: string }) => {
        const { n1 } = mesRequest.gearboxData;
        const type = Number(Object.keys(value));
        const subset: Partial<GearboxData> = { motorDriveConnection: type };

        if (type === MotorDriveConnection.Direct) {
            subset.n2 = n1;
            subset.gearboxInertia = DEFAULT_GEARBOX_INERTIA;

            dispatch(setGearboxInputData({ n2: n1?.toString() ?? '', gearboxInertia: DEFAULT_GEARBOX_INERTIA.toString() }));
            dispatch(setN2Error(true));
            dispatch(setGearboxInertiaError(true));

            dispatch(setTags({ gearboxN2: undefined, gearboxInertia: undefined }));
        }

        dispatch(setTags({ motorDriveConnection: type }));

        await onGearboxChange(subset);
    };


    const handleGearboxInertiaInputBlur = async (value: number) => {
        const { startingCondition } = mesRequest.commonData;
        const gearboxInertia = Number(gearboxInputData.gearboxInertia);
        const gearBoxInertiaMax = calculationResponse && getGearboxInertiaSubtitutedMax(calculationResponse, startingCondition);

        gearboxInertiaError && dispatch(setGearboxInertiaError(false));

        if (gearBoxInertiaMax && gearboxInertia && gearboxInertia > gearBoxInertiaMax) {
            dispatch(setGearboxInputData({ gearboxInertia: gearBoxInertiaMax.toString() }));

            await onGearboxChange({ gearboxInertia: gearBoxInertiaMax });

            showStatusMessage({
                message: translate(TRANSLATIONS.snackbar.info.gearboxInertiaEnteredValueTooHigh),
                showProgress: true,
                type: 'info'
            });
        } else {
            dispatch(setTags({ gearboxInertia: value }));

            await onGearboxChange({ gearboxInertia: value });
        }
    };

    const handleN2InputBlur = async (value: number) => {
        const { n2 } = gearboxInputData;
        const n2AsNumber = Number(n2);
        const n2Max = getN2Max(mesRequest.motorData.speed ?? 0);

        n2Error && dispatch(setN2Error(false));

        if (n2 && n2AsNumber > n2Max) {
            dispatch(setGearboxInputData({ n2: n2Max.toString() }));
            dispatch(setTags({ gearboxN2: n2Max }));

            await onGearboxChange({ n2: n2Max });

            showStatusMessage({
                message: translate(TRANSLATIONS.snackbar.info.n2EnteredValueTooHigh),
                showProgress: true,
                type: 'info'
            });
        } else if (n2 && n2AsNumber < 1) {
            dispatch(setGearboxInputData({ n2: '1' }));
            dispatch(setTags({ gearboxN2: 1 }));

            await onGearboxChange({ n2: 1 });

            showStatusMessage({
                message: translate(TRANSLATIONS.snackbar.info.n2EnteredValueTooLow),
                showProgress: true,
                type: 'info'
            });
        } else {
            dispatch(setTags({ gearboxN2: value }));

            await onGearboxChange({ n2: value });
        }
    };

    const handleLoadInertiaInputBlur = async (value: number) => {
        const { startingCondition } = mesRequest.commonData;
        const loadInertiaAsNumber = Number(setStringDecimalCount(fixNumberZeroFormat(value.toString()), 4));
        const loadInertiaMax = getLoadInertiaMax(calculationResponse, startingCondition);

        loadInertiaError && dispatch(setLoadInertiaError(false));

        if (loadInertiaMax && loadInertiaAsNumber && loadInertiaAsNumber > loadInertiaMax) {
            dispatch(setGearboxInputData({ loadInertia: loadInertiaMax.toString() }));

            await onGearboxChange({ loadInertia: loadInertiaMax });

            showStatusMessage({
                message: translate(TRANSLATIONS.snackbar.info.loadInertiaEnteredValueTooHigh),
                showProgress: true,
                type: 'info'
            });
        } else {
            dispatch(setTags({ loadInertia: loadInertiaAsNumber }));

            await onGearboxChange({ loadInertia: loadInertiaAsNumber });
        }
    };

    const handleGearboxInputChange = (property: keyof GearboxDataInput, value: string) =>     
        dispatch(setGearboxInputData({ [property]: setStringDecimalCount(fixNumberZeroFormat(value), 4) }));

    const showGearboxInertiaDialog = () => {
        const title = translate(TRANSLATIONS.dialog.info.gearboxInertiaValueTooLow.title);
        const content = translate(TRANSLATIONS.dialog.info.gearboxInertiaValueTooLow.text);
        const buttonText = translate(TRANSLATIONS.dialog.info.gearboxInertiaValueTooLow.button);

        showInfoModal({
            title,
            content,
            buttonText,
            onClose: () => dispatch(setGearboxInertiaDialog(false))
        });
    };

    const showLoadInertiaDialog = () => {
        const title = translate(TRANSLATIONS.dialog.info.loadInertiaValueTooLow.title);
        const content = translate(TRANSLATIONS.dialog.info.loadInertiaValueTooLow.text);
        const buttonText = translate(TRANSLATIONS.dialog.info.loadInertiaValueTooLow.button);

        showInfoModal({
            title,
            content,
            buttonText,
            onClose: () => dispatch(setLoadInertiaLowDialog(false))
        });
    };

    const renderDataWithIcon = (iconType: IconType, label: string, value: number) => (
        <div className='connection-row'>
            <Icon type={iconType} />
            <div className='data-container'>
                <div className='label' dangerouslySetInnerHTML={{ __html: label }} />
                <div className='value'>{value}</div>
            </div>
        </div>
    );

    const renderInputWithIcon = (
        iconType: IconType,
        label: string,
        focusText: string,
        value: string,
        onChange: (value: string) => void,
        onBlur?: (value: number) => void,
        error?: boolean
    ) => (
        <div className='connection-row input'>
            <Icon type={iconType} />
            <TextInput
                disabled={disabled}
                error={error}
                label={label}
                name={IconType[iconType]}
                value={value}
                onChange={x => DOUBLE_REGEX.test(x) && onChange(x)}
                onBlur={e => onBlur && onBlur(Number(e.target.value))}
                onFocusText={focusText}
            />
        </div>
    );

    const renderMotorDriveConnection = () => {
        const { motorDriveConnection } = mesRequest.gearboxData;
        const motorDriveConnections: { [key in MotorDriveConnection]?: string }[] = [
            { '0': translate(TRANSLATIONS.filters.operationalData.loadCoupling.motorDriveConnection.WithGearbox) },
            { '1': translate(TRANSLATIONS.filters.operationalData.loadCoupling.motorDriveConnection.Direct) }
        ];

        return (
            <div className='select-with-tooltip'>
                <Select
                    disabled={disabled}
                    label={translate(TRANSLATIONS.filters.operationalData.loadCoupling.motorDriveConnection.title)}
                    value={motorDriveConnections[motorDriveConnection]}
                    values={motorDriveConnections}
                    mapToString={x => Object.values(x).toString()}
                    onSelect={handleMotorDriveConnectionChange}
                />
                <MultilineTooltip idName='gearBoxInfo' text={translate(TRANSLATIONS.tooltips.loadCoupling)} html>
                    <></>
                </MultilineTooltip>
            </div>
        );
    };

    const renderDatas = () => {
        const { motorInertia, n1 } = mesRequest.gearboxData;

        return (
            <>
                {renderDataWithIcon(
                    IconType.Motor,
                    `${translate(TRANSLATIONS.filters.operationalData.loadCoupling.motorInertia)}${unit}`,
                    powerRound(motorInertia ?? 0)
                )}
                {isGearboxConnected && n1 && renderDataWithIcon(
                    IconType.Connect,
                    translate(TRANSLATIONS.filters.operationalData.loadCoupling.n1),
                    n1
                )}
            </>
        );
    };

    const renderInputs = () => {
        const { n2, gearboxInertia, loadInertia } = gearboxInputData;
        const { startingCondition } = mesRequest.commonData;
        const { speed } = mesRequest.motorData;
        const gearboxInertiaMax = calculationResponse && getGearboxInertiaSubtitutedMax(calculationResponse, startingCondition);
        const n2Max = getN2Max(Number(speed));
        const loadInertiaMax = getLoadInertiaMax(calculationResponse, startingCondition);

        return (
            <>
                {isGearboxConnected && renderInputWithIcon(
                    IconType.Gear,
                    `${translate(TRANSLATIONS.filters.operationalData.loadCoupling.gearboxInertia)} ${unit}`,
                    gearboxInertiaMax ? `0 \u2264 ... \u2264 ${gearboxInertiaMax}` : '',
                    gearboxInertia && Number(gearboxInertia) >= 0 ? gearboxInertia : '0',
                    x => handleGearboxInputChange('gearboxInertia', x),
                    handleGearboxInertiaInputBlur,
                    gearboxInertiaError
                )}
                {isGearboxConnected && renderInputWithIcon(
                    IconType.ConnectBlue,
                    translate(TRANSLATIONS.filters.operationalData.loadCoupling.n2),
                    n2Max ? `1 \u2264 ... \u2264 ${n2Max}` : '',
                    n2,
                    x => handleGearboxInputChange('n2', x),
                    handleN2InputBlur,
                    n2Error
                )}
                {renderInputWithIcon(
                    IconType.Pump,
                    `${translate(TRANSLATIONS.filters.operationalData.loadCoupling.loadInertia)} ${unit}`,
                    loadInertiaMax ? `0 \u2264 ... \u2264 ${loadInertiaMax}` : '',
                    loadInertia && Number(loadInertia) >= 0 ? loadInertia : '0',
                    x => handleGearboxInputChange('loadInertia', x),
                    handleLoadInertiaInputBlur,
                    loadInertiaError
                )}
            </>
        );
    };

    return (
        <div className='gearbox-container'>
            <Accordion
                iconFont={'e930'}
                label={translate(TRANSLATIONS.filters.operationalData.loadCoupling.title)}
                open={accordionOpen}
                onClick={() => toggleAccordion(!accordionOpen)}
            >
                <Flex direction={FlexDirection.Column} justification={FlexJustification.Center} gap={15}>
                    {renderMotorDriveConnection()}
                    <img className='gearbox-image' src={isGearboxConnected ? withGearboxImg : gearboxDirectImg} />
                    {renderDatas()}
                    {renderInputs()}
                </Flex>
            </Accordion>
        </div>
    );
};

export default GearboxInputData;
